import { useCallback, useEffect, useRef, useState } from 'react';

import limitedEditionCollectorsService from '@/services/limitedEditionCollectors.service';

const EMPTY_COLLECTORS = Object.freeze({
  owners: [],
  sold_count: 0,
  sold_counts: {},
});

function normaliseCollectors(payload) {
  const owners = Array.isArray(payload?.owners) ? payload.owners : [];
  const soldCounts = payload?.sold_counts && typeof payload.sold_counts === 'object'
    ? payload.sold_counts
    : {};
  const soldCount = Number(payload?.sold_count);

  return {
    owners,
    sold_count: Number.isFinite(soldCount) && soldCount >= 0 ? soldCount : owners.length,
    sold_counts: soldCounts,
  };
}

export default function useLimitedEditionCollectors({
  artworkSlug,
  viewer,
  onAuthenticationRequired,
  autoOpen = false,
}) {
  const [collectors, setCollectors] = useState(EMPTY_COLLECTORS);
  const [ownersPopupOpen, setOwnersPopupOpen] = useState(false);
  const [suartePlusPopupOpen, setSuartePlusPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const autoOpenAttemptRef = useRef(null);

  const viewerId = viewer?._id ?? viewer?.user_id ?? viewer?.uid ?? null;
  const isAuthenticated = Boolean(viewerId);

  useEffect(() => {
    setCollectors(EMPTY_COLLECTORS);
    setOwnersPopupOpen(false);
    setIsLoading(false);
    autoOpenAttemptRef.current = null;
  }, [artworkSlug]);

  const fetchCollectors = useCallback(async () => {
    setIsLoading(true);
    try {
      const { response, data } = await limitedEditionCollectorsService.getLimitedEditionCollectors(artworkSlug);
      if (response?.ok) {
        setCollectors(normaliseCollectors(data));
        setOwnersPopupOpen(true);
        return;
      }

      if (response?.status === 401) {
        onAuthenticationRequired?.();
      } else if (response?.status === 403 && data?.error_type === 'SUARTE_PLUS_REQUIRED') {
        setSuartePlusPopupOpen(true);
      } else {
        console.error('[LimitedEditionCollectors] Access was denied', data);
      }
    } catch (error) {
      console.error('[LimitedEditionCollectors] Unable to load collectors', error);
    } finally {
      setIsLoading(false);
    }
  }, [artworkSlug, onAuthenticationRequired]);

  const openCollectors = useCallback(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      onAuthenticationRequired?.();
      return;
    }

    void fetchCollectors();
  }, [fetchCollectors, isAuthenticated, isLoading, onAuthenticationRequired]);

  useEffect(() => {
    if (!autoOpen || !artworkSlug) {
      return;
    }

    const attemptKey = `${artworkSlug}:${viewerId ?? 'anonymous'}`;
    if (autoOpenAttemptRef.current === attemptKey) {
      return;
    }

    autoOpenAttemptRef.current = attemptKey;
    openCollectors();
  }, [artworkSlug, autoOpen, openCollectors, viewerId]);

  return {
    collectors,
    isLoading,
    openCollectors,
    ownersPopupOpen,
    closeOwnersPopup: () => setOwnersPopupOpen(false),
    suartePlusPopupOpen,
    closeSuartePlusPopup: () => setSuartePlusPopupOpen(false),
    handleSuartePlusActivated: fetchCollectors,
  };
}
